/**
 * Modal dialog.
 *
 * Accessibility is the substance here, not decoration: focus moves into the
 * dialog on open and returns to the trigger on close, Escape closes it, Tab is
 * trapped inside, and the page behind cannot scroll. A modal that traps a
 * keyboard user is worse than no modal.
 */
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button.jsx';
import { ErrorAlert } from './States.jsx';
import { Icon } from './Icon.jsx';

import { useTranslation } from 'react-i18next';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  isOpen,
  onClose,
  onCancel,
  title,
  description,
  children,
  footer,
  actions,
  wide,
  size,
  closeOnBackdrop = true,
}) {
  // `size` is how some screens ask for a wider dialog.
  const widthClass = size === 'xl' || size === 'full' ? 'modal-wide modal-xl' : wide || size === 'lg' || size === 'wide' ? 'modal-wide' : '';
  const dialogRef = useRef(null);
  const titleId = useId();
  const previouslyFocused = useRef(null);
  const handleClose = onClose || onCancel;
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;
  const hasFocusedRef = useRef(false);

  const { t } = useTranslation();
  const isShown = isOpen !== undefined ? Boolean(isOpen) : (open !== undefined ? Boolean(open) : true);
  const footerContent = footer || actions;

  useEffect(() => {
    if (!isShown) {
      hasFocusedRef.current = false;
      return undefined;
    }

    if (!hasFocusedRef.current) {
      // Only capture the trigger if focus is not already inside: otherwise we
      // would record an element within the dialog and "restore" focus to a
      // node that is about to be unmounted.
      if (!dialogRef.current?.contains(document.activeElement)) {
        previouslyFocused.current = document.activeElement;
      }

      // Move focus inside: the first enabled field if there is one, else the
      // dialog. Skipped when focus is already within the dialog, so a modal
      // that re-renders does not yank the caret out of the field being typed
      // in.
      const node = dialogRef.current;
      if (node && !node.contains(document.activeElement)) {
        const firstField = node.querySelector(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );
        (firstField ?? node)?.focus();
      }
      hasFocusedRef.current = true;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      // Keep Tab inside the dialog.
      const focusable = [...dialogRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable.at(-1);

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    // Stop the page behind from scrolling.
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = '';
    };
  }, [isShown]);

  // Restore focus when modal closes/unmounts
  useEffect(() => {
    return () => {
      previouslyFocused.current?.focus?.();
    };
    // Depends on isShown so focus returns to the trigger when the modal is
    // hidden, not only when it unmounts: these dialogs usually stay mounted
    // and toggle `open`.
  }, [isShown]);

  if (!isShown) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) handleClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className={`modal ${widthClass}`}
        role="dialog"
        aria-modal="true"
        // Named by its heading, so a title that is markup (an icon beside the
        // words) is still read as words rather than "[object Object]".
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="modal-header">
          <div>
            <h2 className="card-title" id={titleId}>
              {title}
            </h2>
            {description && (
              <p className="text-sm muted" style={{ marginTop: 2 }}>
                {description}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            aria-label={t('a11y.close')}
            className="modal-close"
            icon={<Icon name="x" size={16} />}
          />
        </div>

        <div className="modal-body">{children}</div>

        {footerContent && <div className="modal-footer">{footerContent}</div>}
      </div>
    </div>,
    document.body
  );
}

/**
 * Confirmation dialog for a destructive or irreversible action.
 *
 * `requireReason` is used where the API demands one — cancelling a purchase
 * order, rejecting a request — so the UI cannot submit something the server is
 * going to refuse.
 */
export function ConfirmDialog({
  open,
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel,
  variant = 'danger',
  destructive,
  loading,
  requireReason = false,
  reasonLabel,
  reasonHint,
  reason = '',
  onReasonChange,
  error,
}) {
  const { t } = useTranslation();
  const handleClose = onClose || onCancel;
  const isShown = isOpen !== undefined ? Boolean(isOpen) : (open !== undefined ? Boolean(open) : true);
  const canConfirm = !requireReason || reason.trim().length > 0;
  const showReason = requireReason || Boolean(onReasonChange);
  const effectiveVariant = destructive ? 'danger' : (variant || 'danger');

  if (!isShown) return null;

  return (
    <Modal
      open={isShown}
      isOpen={isShown}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button onClick={handleClose} disabled={loading}>
            {t('actions.cancel')}
          </Button>
          <Button variant={effectiveVariant} onClick={onConfirm} loading={loading} disabled={!canConfirm}>
            {confirmLabel ?? t('actions.confirm')}
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />

        {message && <p>{message}</p>}

        {showReason && (
          <div className="field">
            <label className={`field-label${requireReason ? ' field-required' : ''}`} htmlFor="confirm-reason">
              {reasonLabel ?? t('confirm.reason')}
            </label>
            <textarea
              id="confirm-reason"
              className="textarea"
              value={reason}
              onChange={(event) => onReasonChange?.(event.target.value)}
              rows={3}
            />
            <span className="field-hint">{reasonHint ?? t('confirm.reasonHint')}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
